import './SpacingInput.css'

import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { useDebounceInput } from '@/shared/makyo-hooks'
import {
  ArrowShortDown16,
  ArrowShortLeft16,
  ArrowShortRight16,
  ArrowShortUp16,
  AspectOne16,
  RadiusAll16,
} from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, SimpleInput } from '@makyoapp/makyo-ui'

import { SpacingInputPosition, SpacingInputUnit, SpacingInputValue } from '@/shared/builder'

import { SpaceFieldWithUnit } from './SpaceFieldWithUnit'

export interface SpacingInputProps {
  id?: string
  values?: SpacingInputValue
  onChange: (nextValues: SpacingInputValue) => void
  max?: {
    [SpacingInputUnit.Pixel]: number
    [SpacingInputUnit.Rem]: number
    [SpacingInputUnit.Em]: number
    [SpacingInputUnit.Percent]: number
  }
}

const SPACING_MIXED = 'Mixed'

export function SpaceFieldMixed({
  id,
  max,
  onChange,
}: {
  id: string
  onChange: (value: number, unit: SpacingInputUnit) => void
  max?: {
    [SpacingInputUnit.Pixel]: number
    [SpacingInputUnit.Rem]: number
    [SpacingInputUnit.Em]: number
    [SpacingInputUnit.Percent]: number
  }
}) {
  const [debVal, cancelDeb, , value, setValue] = useDebounceInput('Mixed')

  useEffect(() => {
    if (debVal) {
      const num = Number(debVal)
      if (!isNaN(num)) {
        onChange(num, SpacingInputUnit.Pixel)
      }
    }
  }, [debVal, onChange])

  return (
    <div className='spi-wrap'>
      <SimpleInput
        before={
          <div className='spi-icon'>
            <AspectOne16 />
          </div>
        }
        id={id}
        className='spi-si'
        type='text'
        inputClassName='spi-input'
        value={value}
        onChange={(e) => {
          const val = e.currentTarget.value
          setValue(val)
        }}
        onBlur={() => {
          const num = Number(value)
          if (!isNaN(num)) {
            cancelDeb()
            onChange(num, SpacingInputUnit.Pixel)
          }
        }}
      />
    </div>
  )
}
export function SpacingInput({
  id,
  values = {
    top: { value: 0, unit: SpacingInputUnit.Pixel },
    right: { value: 0, unit: SpacingInputUnit.Pixel },
    bottom: { value: 0, unit: SpacingInputUnit.Pixel },
    left: { value: 0, unit: SpacingInputUnit.Pixel },
  },
  max,
  onChange,
}: SpacingInputProps) {
  const { t } = useTranslation()

  const handleChange = useCallback(
    (value: number, unit: SpacingInputUnit, position?: SpacingInputPosition) => {
      const copy = { ...values }
      if (position) {
        copy[position] = {
          value,
          unit,
        }
      } else {
        const v = {
          value,
          unit,
        }
        copy.top = v
        copy.bottom = v
        copy.left = v
        copy.right = v
      }
      onChange(copy)
    },
    [onChange, values]
  )

  const [split, setSplit] = useBoolean(false)
  const btn = (
    <Button
      title={t('Split')}
      variant={!split ? ButtonVariant.Ghost : ButtonVariant.Gray}
      squared
      noTip
      noPadding
      icon={<RadiusAll16 />}
      onClick={() => setSplit(!split)}
    />
  )

  const isMixed = useMemo(() => {
    return !Object.values(SpacingInputPosition).every(
      (p) =>
        values[p]?.value === values[SpacingInputPosition.Top]?.value &&
        values[p]?.unit === values[SpacingInputPosition.Top]?.unit
    )
  }, [values])

  return (
    <div id={id} className='spi'>
      {split ? (
        <>
          <SpaceFieldWithUnit
            id={`${id}-top`}
            icon={<ArrowShortUp16 />}
            value={values[SpacingInputPosition.Top]}
            position={SpacingInputPosition.Top}
            onChange={handleChange}
            max={max}
          />
          <SpaceFieldWithUnit
            id={`${id}-right`}
            icon={<ArrowShortRight16 />}
            value={values[SpacingInputPosition.Right]}
            position={SpacingInputPosition.Right}
            onChange={handleChange}
            max={max}
          />
          <SpaceFieldWithUnit
            id={`${id}-bottom`}
            icon={<ArrowShortDown16 />}
            value={values[SpacingInputPosition.Bottom]}
            position={SpacingInputPosition.Bottom}
            onChange={handleChange}
            max={max}
          />
          <SpaceFieldWithUnit
            id={`${id}-left`}
            icon={<ArrowShortLeft16 />}
            value={values[SpacingInputPosition.Left]}
            position={SpacingInputPosition.Left}
            onChange={handleChange}
            max={max}
          />
          {btn}
        </>
      ) : isMixed ? (
        <>
          <SpaceFieldMixed id={`${id}-all`} max={max} onChange={handleChange} />
          {btn}
        </>
      ) : (
        <>
          <SpaceFieldWithUnit
            id={`${id}-all`}
            icon={<AspectOne16 />}
            value={values[SpacingInputPosition.Top]}
            onChange={handleChange}
            max={max}
          />
          {btn}
        </>
      )}
    </div>
  )
}
